import React, {useEffect, useState} from 'react';
import {Link, useNavigate, useParams} from 'react-router-dom';
import Layout from '../components/Layout';
import ConfirmModal from '../../components/ConfirmModal';
import {api} from '../../api';


export default function TaskDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [task, setTask] = useState(null);
  const [subtasks, setSubtasks] = useState([]);
  const [newSubtask, setNewSubtask] = useState('');
  const [newSubtaskAssignee, setNewSubtaskAssignee] = useState('');
  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteSubtaskTarget, setDeleteSubtaskTarget] = useState(null);
  const [users, setUsers] = useState([]);
  const [userMap, setUserMap] = useState({});

  const loadSubtasks = () => api(`/tasks/${id}/subtasks`).then(r => r.json()).then(setSubtasks).catch(() => {});

  useEffect(() => {
    api(`/tasks/${id}`).then(r => r.json()).then(setTask);
    loadSubtasks();
    api('/tenant-users').then(r => r.json()).then(list => {
      setUsers(list);
      const map = {};
      list.forEach(u => { map[u.userId] = `${u.firstName} ${u.lastName}`; });
      setUserMap(map);
    }).catch(() => {});
  }, [id]);

  const handleDelete = async () => {
    await api(`/tasks/${id}`, { method: 'DELETE' });
    navigate('/tasks');
  };

  const handleAddSubtask = async (e) => {
    e.preventDefault();
    if (!newSubtask.trim()) return;
    await api(`/tasks/${id}/subtasks`, { method: 'POST', body: JSON.stringify({ title: newSubtask.trim(), assignedTo: newSubtaskAssignee || null }) });
    setNewSubtask('');
    setNewSubtaskAssignee('');
    loadSubtasks();
  };

  const handleToggle = async (subtask) => {
    await api(`/tasks/${id}/subtasks/${subtask.id}`, { method: 'PUT', body: JSON.stringify({ title: subtask.title, completed: !subtask.completed, assignedTo: subtask.assignedTo }) });
    loadSubtasks();
  };

  const handleSubtaskAssigneeChange = async (subtask, userId) => {
    await api(`/tasks/${id}/subtasks/${subtask.id}`, { method: 'PUT', body: JSON.stringify({ title: subtask.title, completed: subtask.completed, assignedTo: userId || null }) });
    loadSubtasks();
  };

  const handleDeleteSubtask = async () => {
    await api(`/tasks/${id}/subtasks/${deleteSubtaskTarget}`, { method: 'DELETE' });
    setDeleteSubtaskTarget(null);
    loadSubtasks();
  };

  const handleClone = async () => {
    const res = await api(`/tasks/${id}/clone`, { method: 'POST' });
    if (res.ok) {
      const cloned = await res.json();
      navigate(`/tasks/${cloned.id}`);
    }
  };

  if (!task) return null;

  const completed = subtasks.filter(s => s.completed).length;

  return (
    <Layout>
      <Link to="/tasks" className="text-orange-600 hover:text-orange-800 mb-4 inline-block">&larr; Back to Tasks</Link>

      <div className="bg-white rounded-lg shadow mb-6">
        <div className="p-4 md:p-6 border-b">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-gray-900">{task.title}</h1>
            </div>
            <div className="flex items-center gap-3">
              {task.status && (
                <span className={`px-4 py-2 rounded-full text-sm font-medium ${
                  task.status === 'Done' ? 'bg-green-100 text-green-800' :
                  task.status === 'In Progress' ? 'bg-yellow-100 text-yellow-800' :
                  'bg-gray-100 text-gray-800'
                }`}>
                  {task.status}
                </span>
              )}
              <button onClick={handleClone} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">Clone</button>
              <Link to={`/tasks/${id}/edit`} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">Edit</Link>
              <button onClick={() => setDeleteModal(true)} className="px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50">Delete</button>
            </div>
          </div>
        </div>
        <div className="p-4 md:p-6 grid grid-cols-1 sm:grid-cols-4 gap-6">
          <div>
            <p className="text-sm text-gray-500 mb-1">Priority</p>
            <p className="text-gray-900 font-medium">{task.priority || '—'}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500 mb-1">Due Date</p>
            <p className="text-gray-900 font-medium">{task.dueDate || '—'}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500 mb-1">Assigned To</p>
            <p className="text-gray-900 font-medium">{task.assignedTo ? (userMap[task.assignedTo] || task.assignedTo) : '—'}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500 mb-1">Created</p>
            <p className="text-gray-900 font-medium">{new Date(task.createdAt).toLocaleDateString()}</p>
          </div>
        </div>
        {task.description && (
          <div className="px-4 md:px-6 pb-4 md:pb-6">
            <p className="text-sm text-gray-500 mb-1">Description</p>
            <p className="text-gray-900 whitespace-pre-wrap">{task.description}</p>
          </div>
        )}
      </div>

      <div className="bg-white rounded-lg shadow">
        <div className="p-4 md:p-6 border-b">
          <h2 className="text-xl font-semibold text-gray-900">
            Subtasks {subtasks.length > 0 && <span className="text-sm font-normal text-gray-500">({completed}/{subtasks.length})</span>}
          </h2>
        </div>
        <div className="p-4 md:p-6">
          <form onSubmit={handleAddSubtask} className="flex flex-col sm:flex-row gap-3 mb-4">
            <input
              type="text"
              placeholder="Add a subtask..."
              value={newSubtask}
              onChange={(e) => setNewSubtask(e.target.value)}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-600"
            />
            <select
              value={newSubtaskAssignee}
              onChange={(e) => setNewSubtaskAssignee(e.target.value)}
              className="w-full sm:w-auto px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-600"
            >
              <option value="">Unassigned</option>
              {users.map(u => (
                <option key={u.userId} value={u.userId}>{u.firstName} {u.lastName}</option>
              ))}
            </select>
            <button type="submit" className="w-full sm:w-auto px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700">Add</button>
          </form>
          <ul className="divide-y divide-gray-200">
            {subtasks.map((s) => (
              <li key={s.id} className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 py-3 group">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <input
                    type="checkbox"
                    checked={s.completed}
                    onChange={() => handleToggle(s)}
                    className="w-5 h-5 shrink-0 rounded border-gray-300 text-orange-600 focus:ring-orange-600 cursor-pointer"
                  />
                  <span className={`flex-1 truncate ${s.completed ? 'line-through text-gray-400' : 'text-gray-900'}`}>{s.title}</span>
                </div>
                <div className="flex items-center gap-2 pl-8 sm:pl-0">
                  <select
                    value={s.assignedTo || ''}
                    onChange={(e) => handleSubtaskAssigneeChange(s, e.target.value)}
                    className="text-sm px-2 py-1 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-600"
                  >
                    <option value="">Unassigned</option>
                    {users.map(u => (
                      <option key={u.userId} value={u.userId}>{u.firstName} {u.lastName}</option>
                    ))}
                  </select>
                  <button onClick={() => setDeleteSubtaskTarget(s.id)} className="text-gray-400 hover:text-red-600 text-sm">
                    Delete
                  </button>
                </div>
              </li>
            ))}
          </ul>
          {subtasks.length === 0 && (
            <p className="text-gray-500 text-sm mt-4">No subtasks yet.</p>
          )}
        </div>
      </div>

      <ConfirmModal
        open={deleteModal}
        title="Delete Task"
        message="Are you sure you want to delete this task? This action cannot be undone."
        onConfirm={handleDelete}
        onCancel={() => setDeleteModal(false)}
      />

      <ConfirmModal
        open={!!deleteSubtaskTarget}
        title="Delete Subtask"
        message="Are you sure you want to delete this subtask?"
        onConfirm={handleDeleteSubtask}
        onCancel={() => setDeleteSubtaskTarget(null)}
      />
    </Layout>
  );
}
